import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { EnvelopeIcon } from '@heroicons/react/24/outline'

// Components
import { CheckBox } from '../CheckBox'
import { Modal } from '../Modal'

// Utils
import { RESENDABLE_EMAILS } from '../../utils/constants'

/**
 *
 * ResendLicenseEmailsModal
 *
 * Allows an EE admin to resend the standardized event emails (Welcome and License
 * Assigned) to the selected licensed users, regardless of their status.
 */
const ResendLicenseEmailsModal = ({ licenses, loading, onSubmit, setShowModal, showModal }) => {
  const [selectedIdentifiers, setSelectedIdentifiers] = useState(
    RESENDABLE_EMAILS.map((email) => email.identifier),
  )

  const toggleIdentifier = (identifier) =>
    setSelectedIdentifiers((selected) =>
      selected.includes(identifier)
        ? selected.filter((i) => i !== identifier)
        : [...selected, identifier],
    )

  return (
    <Modal
      actions={[
        { type: 'cancel', label: 'Cancel', onClick: () => setShowModal(false) },
        {
          type: 'submit',
          label: `Send to ${licenses.length} ${licenses.length === 1 ? 'User' : 'Users'}`,
          disabled: selectedIdentifiers.length === 0,
          onClick: () => onSubmit(selectedIdentifiers),
        },
      ]}
      icon={<EnvelopeIcon className="h-5 stroke-white sm:h-6" />}
      content={
        <div className="mt-3 flex flex-col gap-4 sm:mt-5">
          <span className="text-sm text-gray-600">
            Select the emails to send. Emails are sent immediately to each selected user, whether
            or not they have logged in before.
          </span>

          <div className="flex flex-col gap-2">
            {RESENDABLE_EMAILS.map((email) => (
              <CheckBox
                key={email.identifier}
                label={email.label}
                name={email.identifier}
                onChange={() => toggleIdentifier(email.identifier)}
                value={selectedIdentifiers.includes(email.identifier)}
              />
            ))}
          </div>

          <div className="max-h-40 overflow-y-auto rounded-lg bg-gray-100 p-3">
            <ul className="space-y-1 text-sm text-gray-600">
              {licenses.map((license) => (
                <li key={license.id}>
                  {license.assignee.user.firstName} {license.assignee.user.lastName} —{' '}
                  {license.assignee.user.email}
                </li>
              ))}
            </ul>
          </div>
        </div>
      }
      loading={loading}
      open={showModal}
      setOpen={setShowModal}
      title="Resend Emails"
    />
  )
}

ResendLicenseEmailsModal.defaultProps = {
  loading: false,
}

ResendLicenseEmailsModal.propTypes = {
  licenses: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  setShowModal: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
}

export default ResendLicenseEmailsModal
